import roundNumber from 'helpers/round-number'
import formatCurrency from 'helpers/new-format-currency'
import * as MomentHelp from 'helpers/moment-helper'
import moment from 'moment'

/** @module MortDataPrep */

/**
 * Returns a loan duration in years for a given schedule (calculates months and rounds up)
 *
 * @param {Object} schedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 *
 * @return {Integer} - the loan duration in years
 *
 * @example
 *
 * returns = 30
 */
export function loanDurationInYears(schedule) {
  const loanDates       = Object.keys(schedule)
  const startDateString = loanDates.shift()
  const endDateString   = loanDates.pop()

  const momentStartDate = moment( MomentHelp.formatForMomentYearFirst( startDateString ) )
  const momentEndDate   = moment( MomentHelp.formatForMomentYearFirst( endDateString ) )

  const numberOfMonths  = momentEndDate.diff(momentStartDate, 'months')

  return Math.ceil( numberOfMonths / 12 )
}


/**
 * Takes two yearly mortgage data sets and a start date to return data suitable for the d3 graph
 *
 * @param {object} originalYearlyData - result of [MortMath.balanceByYearFrom]{@link module:MortMath.balanceByYearFrom}
 * @param {object} newYearlyData - result of [MortMath.balanceByYearFrom]{@link module:MortMath.balanceByYearFrom}
 *
 * @return {array} with three objects representing Yrs, Original Loan and New Loan
 * @example
 *
 * returns = [
 *   {
 *     label: 'Yrs',
 *     data: [ 0, 1, 2, ... ],
 *   },
 *   {
 *     label: 'Original Loan',
 *     data: [ 246984.9614993914, 239541.93773316863, 231795.6736947213, ... ],
 *   },
 *   {
 *     label: 'New Loan',
 *     data: [ 238579.07095882075, 235042.72320823427, 231320.8192421315, ... ]
 *    },
 * ]
 */
export function graphComparisonData(originalYearlyData, newYearlyData) {
  const originalLength = originalYearlyData.length
  const newLength = newYearlyData.length

  const longest = Math.max(originalLength, newLength)

  const yearSet = Array.from(Array(longest).keys())

  return [
    { label: 'Yrs', data: yearSet },
    { label: 'Original Loan', data: originalYearlyData },
    { label: 'New Loan', data: newYearlyData }
  ]
}


/**
 * Takes interestData, principalData, and balanceData for a loan and returns it in suitable format for d3 graph
 *
 * @param {Object} data
 * @param {Array} data.interestData - result of [MortMath.totalInterestAndPrincipalByYear]{@link module:MortMath.totalInterestAndPrincipalByYear}
 * @param {Array} data.principalData - result of [MortMath.totalInterestAndPrincipalByYear]{@link module:MortMath.totalInterestAndPrincipalByYear}
 * @param {Array} data.balanceData - result of [MortMath.attributeOfInterestByYearFrom]{@link module:MortMath.attributeOfInterestByYearFrom}
 *
 * @return {array} with three objects representing Yrs, Original Loan and New Loan
 * @example
 *
 * returns = [
 *   {
 *     label: 'Yrs',
 *     data: [ 0, 1, 2, ... ],
 *   },
 *   {
 *     label: 'Original Loan',
 *     data: [ 246984.9614993914, 239541.93773316863, 231795.6736947213, ... ],
 *   },
 *   {
 *     label: 'New Loan',
 *     data: [ 238579.07095882075, 235042.72320823427, 231320.8192421315, ... ]
 *    },
 * ]
 */
export function graphData({ interestData, principalData, balanceData }) {
  const yearSet = Array.from(Array(interestData.length).keys())

  return [
    { label: 'Yrs', data: yearSet },
    { label: 'Interest', data: interestData },
    { label: 'Principal', data: principalData },
    { label: 'Remaining', data: balanceData }
  ]
}


/**
 * Takes two mortgage schedules and a start date to adds a parameter to call
 * _prepareComparisonTableData for monthly data.
 *
 * @param {object} originalSchedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 * @param {object} newSchedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 * @param {moment} newStartDate
 * @param {object} options - the trigger responsible for starting the table at the beginning vs at new payment
 *
 * @return {object} with monthly array data sets for month, date, payment, interest, principal, and balance.
 * Same return as _prepareTableData
 *
 */
export function monthlyComparisonTableData(originalSchedule, newSchedule, newStartDate, options) {
  return _prepareComparisonTableData(originalSchedule, newSchedule, newStartDate, 'YYYY/M', options)
}


/**
 * Takes two mortgage schedules and a start date to adds a parameter to call
 * _prepareComparisonTableData for yearly data.
 *
 * @param {object} originalSchedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 * @param {object} newSchedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 * @param {moment} newStartDate
 * @param {object} options - the trigger responsible for starting the table at the beginning vs at new payment
 *
 * @return {object} with yearly array data sets for month, date, payment, interest, principal, and balance.
 * Same return as _prepareTableData
 *
 */
export function yearlyComparisonTableData(originalSchedule, newSchedule, newStartDate, options) {
  return _prepareComparisonTableData(originalSchedule, newSchedule, newStartDate, 'YYYY', options)
}


/**
 * Takes a schedule and a monthly payment and adds a date string parameter to call
 * _prepareTableData for monthly data.
 *
 * @param {Object} schedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 * @param {Float} monthlyPayment
 *
 * @return {object} with monthly array data sets for month, date, payment, interest, principal, and balance.
 * Same return as _prepareTableData
 *
 */
export function monthlyTableData(schedule, monthlyPayment) {
  return _prepareTableData(schedule, monthlyPayment, 'YYYY/M')
}


/**
 * Takes a schedule and a monthly payment and adds a date string parameter to call
 * _prepareTableData for yearly data.
 *
 * @param {object} schedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 * @param {Integer} monthlyPayment
 *
 * @return {object} with yearly array data sets for month, date, payment, interest, principal, and balance.
 * Same return as _prepareTableData
 *
 */
export function yearlyTableData(schedule, monthlyPayment) {
  return _prepareTableData(schedule, monthlyPayment, 'YYYY')
}


/**
 * Takes two schedules and calculates the year and month difference between them
 *
 * @param {object} originalSchedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 * @param {object} newSchedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 *
 * @return {object} with year difference and month difference
 *
 * @example
 *
 * return = { yearDiff: integer, monthDiff: integer }
 *
 */
export function loanEndDifference(originalSchedule, newSchedule) {
  const originalKeys      = Object.keys(originalSchedule)
  const newKeys           = Object.keys(newSchedule)
  const originalEndString = originalKeys.pop()
  const newEndString      = newKeys.pop()

  const momentFormatOriginalEnd = MomentHelp.formatForMomentYearFirst(originalEndString)
  const momentFormatNewEnd      = MomentHelp.formatForMomentYearFirst(newEndString)

  const originalEnd = moment( momentFormatOriginalEnd )
  const newEnd      = moment( momentFormatNewEnd )

  // https://stackoverflow.com/a/33988284
  const years = originalEnd.diff(newEnd, 'year')
  newEnd.add(years, 'years')

  const months = originalEnd.diff(newEnd, 'months')

  return { yearDiff: years, monthDiff: months }
}


/**
 * Takes a schedule and returns its end date formatted
 *
 * @param {object} schedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 *
 * @return {String} - the full month and year of the last payment date
 *
 * @example
 *
 * return = 'July 2048'
 *
 */
export function loanEnd(schedule) {
  const keys                = Object.keys(schedule)
  const scheduleEndString   = keys.pop()
  const momentFormatEndDate = MomentHelp.formatForMomentYearFirst(scheduleEndString)
  const loanEnd             = moment(momentFormatEndDate)

  return loanEnd.format('MMMM YYYY')
}


// private


/**
 * Prepares yearly or monthly comparison table data for consumption by the d3 amortization table
 *
 * @param {object} originalSchedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 * @param {object} newSchedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 * @param {moment} newStartDate
 * @param {string} dateFormat - the trigger responsible for yearly or monthly... 'YYYY' or 'YYYY/M'
 * @param {object} options - the trigger responsible for starting the table at the beginning vs at new payment
 *
 * @return {object} with monthly/yearly array data sets for time, date, payment, interest, principal, and balance.
 *
 * @example
 *
 * return = {
 *   timeUnit: ['Month'],
 *   balanceData: ['$249,401', '$239,718', '$248,800', ...],
 *   dateData: ['8/18', '8/18', '9/18', ...],
 *   interestData: ['$833', '$1,025', '$831', ...],
 *   timeData: ['1', '1', '2', '2', '3', '3', ...],
 *   paymentData: ['Original', 'New', 'Original', 'New', ...],
 *   principalData: ['$599', '$282', '$601', ...],
 *   listItemClass: 'amortization-table__item-every-other',
 * }
 */
function _prepareComparisonTableData(originalSchedule, newSchedule, newStartDate, dateFormat, options) {
  const timeUnit      = dateFormat === 'YYYY' ? [ 'Year' ] : [ 'Month' ]
  const keys          = Object.keys(originalSchedule)
  const formattedDate = newStartDate.format(dateFormat)
  const indexToCut    = keys.indexOf(formattedDate)
  const filteredKeys  = keys.slice(indexToCut)
  const originalKeys  = options.useFiltered ? filteredKeys : keys
  const newKeys       = Object.keys(newSchedule)

  const longest = Math.max(originalKeys.length, newKeys.length)

  const originalTimeData      = []
  const originalDateData      = []
  const originalInterestData  = []
  const originalPrincipalData = []
  const originalBalanceData   = []

  originalKeys.forEach((mortgageTimeUnit, i) => {
    const timeUnit  = (i + 1).toString()
    const date      = originalSchedule[mortgageTimeUnit].displayDate
    const interest  = formatCurrency( roundNumber( originalSchedule[mortgageTimeUnit].interest, 0 ) )
    const principal = formatCurrency( roundNumber( originalSchedule[mortgageTimeUnit].principal, 0 ) )
    const balance   = formatCurrency( roundNumber( originalSchedule[mortgageTimeUnit].balance, 0 ) )

    originalTimeData.push( timeUnit )
    originalDateData.push( date )
    originalInterestData.push( interest )
    originalPrincipalData.push( principal )
    originalBalanceData.push( balance )
  })

  const newTimeData      = []
  const newDateData      = []
  const newInterestData  = []
  const newPrincipalData = []
  const newBalanceData   = []

  newKeys.forEach((mortgageTimeUnit, i) => {
    const timeUnit  = (i + 1).toString()
    const date      = newSchedule[mortgageTimeUnit].displayDate
    const interest  = formatCurrency( roundNumber( newSchedule[mortgageTimeUnit].interest, 0 ) )
    const principal = formatCurrency( roundNumber( newSchedule[mortgageTimeUnit].principal, 0 ) )
    const balance   = formatCurrency( roundNumber( newSchedule[mortgageTimeUnit].balance, 0 ) )

    newTimeData.push( timeUnit )
    newDateData.push( date )
    newInterestData.push( interest )
    newPrincipalData.push( principal )
    newBalanceData.push( balance )
  })

  // fill out arrays with ''
  const filledOriginalPaymentData   = Array(longest).fill('Original')
  const filledOriginalInterestData  = fill(originalInterestData, longest, '')
  const filledOriginalPrincipalData = fill(originalPrincipalData, longest, '')
  const filledOriginalBalanceData   = fill(originalBalanceData, longest, '')
  const filledNewPaymentData        = Array(longest).fill('New')
  const filledNewInterestData       = fill(newInterestData, longest, '')
  const filledNewPrincipalData      = fill(newPrincipalData, longest, '')
  const filledNewBalanceData        = fill(newBalanceData, longest, '')

  // use longest for timeUnit and date
  let longestTimeData
  let longestDateData

  if (originalTimeData.length > newTimeData.length) {
    longestTimeData = originalTimeData
    longestDateData = originalDateData
  } else {
    longestTimeData = newTimeData
    longestDateData = newDateData
  }

  // interleave arrays
  const timeData      = interleave(longestTimeData, longestTimeData)
  const dateData      = interleave(longestDateData, longestDateData)
  const paymentData   = interleave(filledOriginalPaymentData, filledNewPaymentData)
  const interestData  = interleave(filledOriginalInterestData, filledNewInterestData)
  const principalData = interleave(filledOriginalPrincipalData, filledNewPrincipalData)
  const balanceData   = interleave(filledOriginalBalanceData, filledNewBalanceData)
  const listItemClass = 'amortization-table__item--border-every-other'

  return {
    timeUnit,
    timeData,
    dateData,
    paymentData,
    interestData,
    principalData,
    balanceData,
    listItemClass
  }
}


/**
 * Prepares yearly or monthly table data for consumption by the d3 amortization table
 *
 * @param {Object} loanSchedule - result of [MortMath.amortizationSchedule]{@link module:MortMath.amortizationSchedule}
 * @param {Integer} monthlyPayment
 * @param {string} dateFormat - the trigger responsible for yearly or monthly... 'YYYY' or 'YYYY/M'
 *
 * @return {object} with monthly/yearly array data sets for time, date, payment, interest, principal, and balance.
 *
 * @example
 *
 * return = {
 *   timeUnit: ['Month'],
 *   balanceData: ['$249,401', '$239,718', '$248,800', ...],
 *   dateData: ['8/18', '8/18', '9/18', ...],
 *   interestData: ['$833', '$1,025', '$831', ...],
 *   timeData: ['1', '1', '2', '2', '3', '3', ...],
 *   paymentData: ['Original', 'New', 'Original', 'New', ...],
 *   principalData: ['$599', '$282', '$601', ...],
 *   listItemClass: 'amortization-table__item-every-other',
 * }
 */
function _prepareTableData(loanSchedule, monthlyPayment, dateFormat) {
  const timeUnit = dateFormat === 'YYYY' ? [ 'Year' ] : [ 'Month' ]
  const keys     = Object.keys(loanSchedule)

  const timeData      = []
  const dateData      = []
  const interestData  = []
  const principalData = []
  const balanceData   = []

  keys.forEach((mortgageTimeUnit, i) => {
    const timeUnit  = (i + 1).toString()
    const date      = loanSchedule[mortgageTimeUnit].displayDate
    const interest  = formatCurrency( roundNumber( loanSchedule[mortgageTimeUnit].interest, 0 ) )
    const principal = formatCurrency( roundNumber( loanSchedule[mortgageTimeUnit].principal, 0 ) )
    const balance   = formatCurrency( roundNumber( loanSchedule[mortgageTimeUnit].balance, 0 ) )

    timeData.push( timeUnit )
    dateData.push( date )
    interestData.push( interest )
    principalData.push( principal )
    balanceData.push( balance )
  })

  const lengthToFill            = Object.keys(timeData).length
  const formattedMonthlyPayment = formatCurrency( roundNumber( monthlyPayment, 0 ) )

  const paymentData   = Array(lengthToFill).fill(formattedMonthlyPayment)
  const listItemClass = 'amortization-table__item--border-every'

  return {
    timeUnit,
    timeData,
    dateData,
    paymentData,
    interestData,
    principalData,
    balanceData,
    listItemClass
  }
}


/**
 * Helper for _prepareTableData that takes two equally sized arrays and interleaves them.
 *
 * @param {array} firstArray
 * @param {array} secondArray
 *
 * @return {array} The combined interleaved array
 *
 * @example
 * firstArray  = [1, 2, 3, 4, 5]
 * secondArray = ['a', 'b', 'c', 'd', 'e']
 *
 * return = [1, 'a', 2, 'b', 3, 'c', 4, 'd', 5, 'e']
 *
 */
function interleave(firstArray, secondArray) {
  const firstLength = firstArray.length

  if (firstLength === secondArray.length) {
    const interleaved = []

    for (let i = 0; i < firstLength; i++) {
      interleaved.push(firstArray[i], secondArray[i])
    }

    return interleaved
  }
}


/**
 * Helper for _prepareTableData that takes an array and fills it with the desired entity
 * to the desired final array length
 *
 * @param {Array} array - array to be filled
 * @param {Integer} lengthToFill - desired final length
 * @param {Any} filler - entity to fill out the array
 *
 * @return {array} The array filled with the entity
 *
 * @example
 * array        = [1, 2, 3, 4, 5]
 * lengthToFill = 10
 * filler       = ''
 *
 * return = [1, 2, 3, 4, 5, '', '', '', '', '']
 *
 */
function fill(array, lengthToFill, filler) {
  const length = array.length
  array.length = lengthToFill

  return array.fill(filler, length)
}
